import { useRef, useState } from 'react'
import { Cloud, Moon, Sun, Sunrise, Sunset, X } from 'lucide-react'
import { useI18n } from '../lib/i18n'
import { useSceneryChoice } from '../bughunt/useRoomScenery'
import { IMAGE_RULES, prepareImage, TIMES, type SceneryChoice, type TimeOfDay } from '../bughunt/scenery'

const ICONS: Record<TimeOfDay, typeof Sun> = { dawn: Sunrise, day: Sun, afternoon: Cloud, sunset: Sunset, night: Moon }

/** The most a viewer's own photo may be before it is shrunk in the browser. */
const MAX_OWN_UPLOAD = 15 * 1024 * 1024

/**
 * How the room looks for this viewer: following their clock, a fixed hour, the organization's
 * picture, or their own — which stays in their browser and is never uploaded.
 */
export function SceneryPicker() {
  const { t } = useI18n()
  const state = useSceneryChoice()
  const fileRef = useRef<HTMLInputElement>(null)
  const [note, setNote] = useState<string | null>(null)

  if (!state) return null

  const { scenery, choice } = state
  const Icon = ICONS[scenery.time]

  const onFile = async (file: File | undefined) => {
    setNote(null)
    if (!file) return
    if (!(IMAGE_RULES.types as readonly string[]).includes(file.type) || file.size > MAX_OWN_UPLOAD) {
      setNote(t('scenery.error.type'))
      return
    }
    try {
      const prepared = await prepareImage(file)
      if (!state.setMyImage(prepared.dataUrl)) {
        setNote(t('scenery.error.storage'))
        return
      }
      state.setChoice('my-image')
      if (prepared.width < IMAGE_RULES.minWidth || prepared.height < IMAGE_RULES.minHeight) setNote(t('scenery.small'))
    } catch {
      setNote(t('scenery.error.type'))
    }
  }

  return (
    <span className="row row--tight">
      <label className="scenery-picker" title={t('scenery.hint')}>
        <Icon size={13} aria-hidden />
        <select
          value={choice}
          aria-label={t('scenery.label')}
          onChange={(event) => {
            const value = event.target.value
            if (value === 'upload') {
              fileRef.current?.click()
              return
            }
            state.setChoice(value as SceneryChoice)
          }}
        >
          <option value="auto">{t('scenery.auto', { time: t(`scenery.time.${scenery.time}`) })}</option>
          {TIMES.map((time) => (
            <option key={time} value={time}>{t(`scenery.time.${time}`)}</option>
          ))}
          {state.hasOrgImage && <option value="org-image">{t('scenery.orgImage')}</option>}
          {state.hasMyImage && <option value="my-image">{t('scenery.myImage')}</option>}
          <option value="upload">{state.hasMyImage ? t('scenery.replaceMyImage') : t('scenery.chooseMyImage')}</option>
        </select>
      </label>

      {choice === 'my-image' && (
        <button
          type="button"
          className="bughunt__icon-button"
          onClick={() => {
            state.setMyImage(null)
            state.setChoice('auto')
            setNote(null)
          }}
          title={t('scenery.removeMyImage')}
          aria-label={t('scenery.removeMyImage')}
        >
          <X size={13} />
        </button>
      )}

      {scenery.holiday && <span className="scenery-holiday tiny">{t(`scenery.holiday.${scenery.holiday}`)}</span>}
      {note && <span className="scenery-note" role="status">{note}</span>}

      <input
        ref={fileRef}
        type="file"
        accept={IMAGE_RULES.types.join(',')}
        hidden
        onChange={(event) => {
          void onFile(event.target.files?.[0])
          event.target.value = ''
        }}
      />
    </span>
  )
}
